// @flow
import React from 'react';
import styled from 'styled-components';
import { FaArrowUp } from 'react-icons/fa';
import LinkedinIcon from '../assets/images/linkedIn.svg';
import TwitterIconImg from '../assets/images/twitter.svg';
import InstagramIconImg from '../assets/images/instagram.svg';
import MediumIconImg from '../assets/images/medium.svg';
import YoutubeIconImg from '../assets/images/youtubeIcon.svg';
import PictogramFile from '../assets/pictogram/gray.png';
import type { RichText as RichTextType } from '../utils/types';
import { isRichText, sanitizeRichText } from '../utils/types';
import { RichText } from 'prismic-reactjs';
import { Info } from '../elements';
import { unescape } from 'lodash';
import Link from '../utils/link';

type Props = {
  about: string,
  address: RichTextType,
  links: [
    {
      item: string,
      itemLink: string,
    }
  ],
  contactUs: string,
  downloadLink: string,
  downloadLinkTxt: string,
  newsletterLink?: string,
  newsletterText?: string,
  smallScreenFooterOptions: [
    {
      item: string,
      itemLink: string,
    }
  ],
  socialIcons: [
    {
      social: 'instagram' | 'medium' | 'linkedin' | 'twitter' | 'medium',
      link: string,
    }
  ],
};

type State = {};

const MAXLINKS = 5;

const FooterBigScreen = styled.footer.attrs({
  className: 'px-6 hidden overflow-hidden lg:block my-20',
})``;

const RowMaxWidth = styled.div.attrs({
  className: 'max-w-screen-xl mx-auto',
})``;

const Row = styled.div.attrs({
  className: 'flex flex-row pt-20 mx-10 lg:-mx-10',
})``;

const Line = styled.div.attrs({
  className: 'bg-nbt-gray mx-16 lg:mx-0',
})`
  border: 1px solid #f4f5f6;
`;

const Column = styled.div.attrs({
  className: 'flex-1 mx-10',
})``;

const LastColumn = styled.div.attrs({
  className: 'flex-1 mx-10 text-right',
})``;

const ColumnLinkContainer = styled.div.attrs({
  className: 'flex mr-12',
})``;

const ColumnLink = styled.div.attrs({
  className: 'flex-auto mx-8',
})``;

const Title = styled(Info).attrs({
  className: 'text-nbt-anthrazit py-1',
})`
  &:not(:first-of-type) {
    margin-top: 1rem;
  }
`;

const AboutBody = styled(Info).attrs({
  className: 'text-base text-nbt-anthrazit font-europa leading-loose mt-4',
})``;

const AboutFooter = styled.div.attrs({
  className:
    'flex flex-row items-center text-base text-nbt-anthrazit font-europa leading-loose mt-8',
})``;

const Pictogram = styled.img.attrs({
  src: PictogramFile,
  className: 'w-8 h-8 mr-3',
})``;

const LinkContainer = styled.div.attrs({
  className: 'text-base text-nbt-anthrazit font-europa',
})``;

const ContactBody = styled.div.attrs({
  className: 'text-base text-nbt-anthrazit font-europa leading-loose mt-6',
})``;

const AddressBody = styled.address.attrs({
  className: 'text-base text-nbt-anthrazit font-europa leading-loose mt-4',
})``;

const SocialMediaIcon = styled.img.attrs({
  className: 'h-8',
})``;

const LinkedInIcon = styled(SocialMediaIcon).attrs({
  src: LinkedinIcon,
})``;

const TwitterIcon = styled(SocialMediaIcon).attrs({
  src: TwitterIconImg,
})``;

const MediumIcon = styled(SocialMediaIcon).attrs({
  src: MediumIconImg,
})``;

const InstagramIcon = styled(SocialMediaIcon).attrs({
  src: InstagramIconImg,
})``;

const YoutubeIcon = styled(SocialMediaIcon).attrs({
  src: YoutubeIconImg,
})`
  -webkit-transform: rotate(90deg);
  -moz-transform: rotate(90deg);
  -o-transform: rotate(90deg);
  -ms-transform: rotate(90deg);
  transform: rotate(90deg);
`;

const NewsletterLink = styled(Link).attrs({
  className: 'text-base text-nbt-anthrazit font-europa underline',
})``;

const SocialIcons = styled.div.attrs({
  className: 'flex flex-row justify-end mt-8',
})``;

const ArrowUpIcon = styled(FaArrowUp).attrs({
  className: 'text-nbt-red ml-3 float-right',
  size: '1em',
})``;

const FooterSmallScreen = styled.footer.attrs({
  className: 'block mb-5 border-t-2 border-gray-300 lg:hidden',
})``;

const PictogramSmallScreen = styled.p.attrs({
  className:
    'flex flex-row justify-center text-base text-nbt-anthrazit font-europa leading-loose mt-6',
})``;

const LinksSmallScreen = styled.div.attrs({
  className: 'flex flex-row mx-8 mt-8',
})``;

const LinkSmallScreen = styled(Link).attrs({
  className:
    'flex-1 text-center text-sm text-nbt-anthrazit font-europa leading-loose',
})``;

const LinkParagraph = styled(Link).attrs({
  className:
    'flex items-center text-base text-nbt-anthrazit font-europa cursor-pointer py-1',
})``;

const LinkSocial = styled(Link).attrs({
  className: 'ml-3 rounded-full blurred-outline',
  title: {},
})``;

const LinkToTopRow = styled.div.attrs({
  className: 'flex flex-row mr-16 justify-end',
})``;

const LinkToTop = styled.a.attrs({
  className:
    'flex items-center text-base text-nbt-anthrazit font-europa cursor-pointer py-1',
})``;

const getLinkTitleFromSocialMediumString = (str) => {
  const stringToTitleMap = {
    instagram: 'Instagram',
    linkedin: 'LinkedIn',
    medium: 'Medium',
    twitter: 'Twitter',
    youtube: 'YouTube',
  };
  return stringToTitleMap[str] || '';
};

class Footer extends React.Component<Props, State> {
  render() {
    const {
      about,
      address,
      contactUs,
      downloadLink,
      downloadLinkTxt,
      links,
      newsletterLink,
      newsletterText,
      smallScreenFooterOptions,
      socialIcons,
    } = this.props;
    return (
      <>
        <FooterBigScreen>
          <RowMaxWidth>
            <Line />
            <Row>
              <Column>
                <Title>About</Title>
                <AboutBody>{unescape(about)}</AboutBody>
                <AboutFooter>
                  <Pictogram />
                  <Info>Copyright NBT AG</Info>
                </AboutFooter>
              </Column>
              <ColumnLinkContainer>
                <ColumnLink>
                  {links.slice(0, MAXLINKS).map(({ item, itemLink }) => (
                    <LinkContainer key={item}>
                      <LinkParagraph to={itemLink}>{item}</LinkParagraph>
                    </LinkContainer>
                  ))}
                </ColumnLink>
                {/* In case of more than 5 links */}
                {links.length > MAXLINKS && (
                  <ColumnLink>
                    {links.slice(MAXLINKS).map(({ item, itemLink }) => (
                      <LinkContainer key={item}>
                        <LinkParagraph to={itemLink}>{item}</LinkParagraph>
                      </LinkContainer>
                    ))}
                  </ColumnLink>
                )}
              </ColumnLinkContainer>
              <Column>
                <Title>Contact Us</Title>
                <ContactBody>
                  <Info>{contactUs}</Info>
                  <Link to={downloadLink}>{downloadLinkTxt}</Link>
                  <Title>Address</Title>
                  {isRichText(address) && (
                    <AddressBody>
                      <Info>{RichText.render(sanitizeRichText(address))}</Info>
                    </AddressBody>
                  )}
                </ContactBody>
              </Column>
              <LastColumn>
                <Title>Never miss the next big news!</Title>
                {newsletterLink && (
                  <NewsletterLink to={newsletterLink}>
                    {newsletterText || 'Sign up to our newsletter now'}
                  </NewsletterLink>
                )}
                <SocialIcons>
                  {socialIcons.map(({ social, link }) => (
                    <LinkSocial
                      key={social}
                      title={getLinkTitleFromSocialMediumString(social)}
                      to={link}
                    >
                      {
                        {
                          linkedin: <LinkedInIcon />,
                          twitter: <TwitterIcon />,
                          instagram: <InstagramIcon />,
                          youtube: <YoutubeIcon />,
                          medium: <MediumIcon />,
                        }[social]
                      }
                    </LinkSocial>
                  ))}
                </SocialIcons>
              </LastColumn>
            </Row>
            <LinkToTopRow>
              <LinkToTop
                onClick={() => {
                  window.scrollTo(0, 0);
                }}
              >
                Back to Top
                <ArrowUpIcon />
              </LinkToTop>
            </LinkToTopRow>
          </RowMaxWidth>
        </FooterBigScreen>
        <FooterSmallScreen>
          <PictogramSmallScreen>
            <Pictogram />
            Copyright NBT AG
          </PictogramSmallScreen>
          <LinksSmallScreen>
            {smallScreenFooterOptions.map(({ item, itemLink }) => (
              <LinkSmallScreen key={item} to={itemLink}>
                {item}
              </LinkSmallScreen>
            ))}
          </LinksSmallScreen>
        </FooterSmallScreen>
      </>
    );
  }
}

export default Footer;
